import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCanEdit } from "@/lib/permissions";
import { Plus, Hammer, Ship, CalendarDays } from "lucide-react";

export const PROJECT_STATUS_META = {
  design: { label: "Design", className: "bg-[#E6F2FA] text-[#0088CC]" },
  in_build: { label: "In Build", className: "bg-amber-100 text-amber-700" },
  sea_trials: { label: "Sea Trials", className: "bg-violet-100 text-violet-700" },
  delivered: { label: "Delivered", className: "bg-emerald-100 text-emerald-700" },
};

export const fmtMoney = (v, cur) =>
  v == null
    ? "—"
    : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${cur || "AUD"}`;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function Projects() {
  const navigate = useNavigate();
  const canEdit = useCanEdit();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", client_id: null, vessel_id: null, status: "design", start_date: "", delivery_date: "" });

  const load = () =>
    Promise.all([api.get("/projects"), api.get("/clients"), api.get("/vessels")])
      .then(([p, c, v]) => {
        setProjects(p.data);
        setClients(c.data);
        setVessels(v.data);
      })
      .catch(() => toast.error("Could not load projects"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.name.trim()) {
      toast.error("Give the project a name");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/projects", {
        ...form,
        start_date: form.start_date || null,
        delivery_date: form.delivery_date || null,
      });
      toast.success("Project created");
      setOpen(false);
      navigate(`/projects/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1200px]" data-testid="projects-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#011836]">Projects</h1>
          <p className="text-sm text-[#5B6B7C] mt-1">Track every build from contract to delivery.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setOpen(true)} className="bg-[#0088CC] hover:bg-[#006DA6] text-white" data-testid="new-project-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New project
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-[#5B6B7C]">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="sos-surface rounded-lg p-10 text-center" data-testid="projects-empty">
          <Hammer className="h-8 w-8 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-sm text-[#5B6B7C]">
            No projects yet. Create one here, or convert an accepted quote from the quote editor.
          </p>
        </div>
      ) : (
        <div className="sos-surface rounded-lg overflow-hidden" data-testid="projects-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#5B6B7C] border-b border-[#E8EEF3]">
                  <th className="px-4 py-3 font-semibold">Project</th>
                  <th className="px-4 py-3 font-semibold">Client</th>
                  <th className="px-4 py-3 font-semibold">Vessel</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold w-[140px]">Progress</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Due delivery</th>
                  <th className="px-4 py-3 font-semibold text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {[...projects]
                  .sort((a, b) => {
                    if (!a.delivery_date && !b.delivery_date) return 0;
                    if (!a.delivery_date) return 1;
                    if (!b.delivery_date) return -1;
                    return new Date(a.delivery_date) - new Date(b.delivery_date);
                  })
                  .map((p) => {
                    const meta = PROJECT_STATUS_META[p.status] || PROJECT_STATUS_META.design;
                    return (
                      <tr
                        key={p.id}
                        onClick={() => navigate(`/projects/${p.id}`)}
                        className="border-b border-[#E8EEF3] last:border-0 hover:bg-[#F4F8FB] cursor-pointer transition-colors"
                        data-testid={`project-row-${p.id}`}
                      >
                        <td className="px-4 py-3">
                          <span className="font-display font-semibold text-[#011836]">{p.name}</span>
                        </td>
                        <td className="px-4 py-3 text-[#5B6B7C]">{p.client_name || "—"}</td>
                        <td className="px-4 py-3 text-[#5B6B7C]">
                          {p.vessel_name ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Ship className="h-3.5 w-3.5" /> {p.vessel_name}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${meta.className}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-2 flex-1 rounded-full bg-[#E8EEF3] overflow-hidden min-w-[60px]">
                              <div className="h-full rounded-full bg-[#0088CC]" style={{ width: `${p.progress}%` }} />
                            </div>
                            <span className="text-xs font-semibold text-[#011836] tabular-nums">{p.progress}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-[#011836]">
                          <span className="inline-flex items-center gap-1.5">
                            <CalendarDays className="h-3.5 w-3.5 text-[#5B6B7C]" /> {fmtDate(p.delivery_date)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-[#011836] tabular-nums whitespace-nowrap">
                          {fmtMoney(p.contract_value, p.currency)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#DDE5EC] text-[#011836] max-w-lg" data-testid="new-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">New project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Project name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Huon 47M Well Boat build"
                className="mt-1.5 bg-white border-[#DDE5EC]"
                data-testid="project-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Client</Label>
                <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? null : v })}>
                  <SelectTrigger className="mt-1.5 bg-white border-[#DDE5EC]" data-testid="project-client-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                    <SelectItem value="none">— None —</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Vessel</Label>
                <Select value={form.vessel_id || "none"} onValueChange={(v) => setForm({ ...form, vessel_id: v === "none" ? null : v })}>
                  <SelectTrigger className="mt-1.5 bg-white border-[#DDE5EC]" data-testid="project-vessel-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#DDE5EC] text-[#011836]">
                    <SelectItem value="none">— None —</SelectItem>
                    {vessels.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5 bg-white border-[#DDE5EC]" data-testid="project-start-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5B6B7C]">Delivery date</Label>
                <Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="mt-1.5 bg-white border-[#DDE5EC]" data-testid="project-delivery-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setOpen(false)} className="border-[#CBD5E1] bg-white text-[#334155]">Cancel</Button>
              <Button onClick={create} disabled={saving} className="bg-[#0088CC] hover:bg-[#006DA6] text-white" data-testid="project-create-btn">
                {saving ? "Creating…" : "Create project"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
